import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

interface RelatedProfile {
  username: string;
  profilePicUrl: string | null;
  isPrivate: boolean;
}

interface SeedResult {
  bentoBrand: {
    id: number;
    name: string | null;
    instagramUsername: string;
    countryCode: string | null;
    nichenessScore: number | null;
    instagramFollowerCount: number | null;
  };
  relatedProfiles: RelatedProfile[];
}

const formatFollowers = (count: number | null): string => {
  if (count == null) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
};

const SeedingPreview = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [configJson, setConfigJson] = useState<string>("");
  const [results, setResults] = useState<SeedResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    fetchDefaultConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDefaultConfig = async () => {
    setConfigLoading(true);
    try {
      const data = await fetcherAuth(
        getToken,
        "/api/admin/potential-bento-brands/instagram-seeding-config",
        "GET",
      );
      setConfigJson(JSON.stringify(data, null, 2));
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setConfigLoading(false);
    }
  };

  const handlePreview = async () => {
    let config;
    try {
      config = JSON.parse(configJson);
    } catch {
      setAlert("Invalid JSON", "warning");
      return;
    }

    setLoading(true);
    try {
      const { results: previewResults } = await fetcherAuth(
        getToken,
        "/api/admin/potential-bento-brands/preview-instagram-seeding",
        "POST",
        {},
        config,
      );
      setResults(previewResults);
      if (previewResults.length === 0) {
        setAlert("No seed candidates found for this config", "warning");
      }
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2 }}>
          <Typography variant="h3" gutterBottom>
            <strong>Instagram Scraping Preview</strong>
          </Typography>
          <Box
            sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}
          >
            <LoadingButton
              variant="outlined"
              loading={configLoading}
              onClick={fetchDefaultConfig}
            >
              Load Current Config
            </LoadingButton>
          </Box>

          <TextField
            multiline
            fullWidth
            minRows={10}
            maxRows={20}
            value={configJson}
            onChange={(e) => setConfigJson(e.target.value)}
            placeholder='{"query": {"function_score": {...}}, "size": 10}'
            inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
            sx={{ mb: 2 }}
          />

          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handlePreview}
            disabled={!configJson.trim()}
          >
            Preview
          </LoadingButton>

          {results !== null && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom>
                {results.length} Seed Candidate{results.length !== 1 ? "s" : ""}
              </Typography>
              <Grid container spacing={2}>
                {results.map((result) => (
                  <Grid item xs={12} md={6} key={result.bentoBrand.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {result.bentoBrand.name ?? "(unnamed)"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="a"
                              href={`https://instagram.com/${result.bentoBrand.instagramUsername}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: "none" }}
                            >
                              @{result.bentoBrand.instagramUsername}
                            </Typography>
                          </Box>
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {result.bentoBrand.countryCode && (
                              <Chip
                                label={result.bentoBrand.countryCode}
                                size="small"
                              />
                            )}
                            <Chip
                              label={`${formatFollowers(result.bentoBrand.instagramFollowerCount)} followers`}
                              size="small"
                              variant="outlined"
                            />
                            {result.bentoBrand.nichenessScore != null && (
                              <Chip
                                label={`nicheness ${result.bentoBrand.nichenessScore}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 0.5 }}
                        >
                          {result.relatedProfiles.length} related profile
                          {result.relatedProfiles.length !== 1 ? "s" : ""}
                          {result.relatedProfiles.length === 0
                            ? " (no related profiles)"
                            : ""}
                        </Typography>

                        {result.relatedProfiles.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {result.relatedProfiles.map((profile) => (
                              <Chip
                                key={profile.username}
                                label={
                                  profile.isPrivate
                                    ? `@${profile.username} 🔒`
                                    : `@${profile.username}`
                                }
                                size="small"
                                component="a"
                                href={`https://instagram.com/${profile.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                                variant="outlined"
                                sx={{ fontSize: 11 }}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Grid>
      }
    />
  );
};

export default SeedingPreview;
